import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { LoadingButton } from "@/components/ui/common/loading-button";
import { Sheet, SheetContent, SheetHeader } from "@/components/ui/sheet";
import { useEffect, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { RootState } from "@/redux/store";
import { useSelector } from "react-redux";
import { FormProps } from "@/types";
import Combobox from "@/components/ui/common/combobox";
import { getApiErrorMessage } from "@/utils/apiError";
import { PriceMaster, PriceMasterStatus, ItemType } from "@/types/system-setting/priceMaster";
import { cn } from "@/lib/utils";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useCreatePriceMasterMutation,
  useUpdatePriceMasterMutation,
  useGetPriceMasterByIdQuery,
} from "@/services/priceMasterApi";
import { Card } from "@/components/ui/card";
import PricingConfig from "./pricing/PricingConfig";
import { useGetBusinessUnitsQuery } from "@/services/businessUnitApi";
import { useStaffBusinessUnits } from "@/hooks/use-staff-business-units";

const NO_BUSINESS_UNIT = "none";

const PriceMasterForm = ({
  isOpen,
  setOpen,
  data,
  action,
  setAction,
  setData,
  refetch,
  readOnly = false,
}: FormProps<PriceMaster>) => {
  const orgId = useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;
  const { toast } = useToast();

  const { isLimitedToBusinessUnits } = useStaffBusinessUnits();

  // For staff limited to some business units the API returns only their live units;
  // everyone else gets every unit of the organization.
  const { data: businessUnitsData } = useGetBusinessUnitsQuery(
    { org_id: Number(orgId), limit: 100 },
    { skip: !orgId || !isOpen }
  );
  const businessUnits = Array.isArray(businessUnitsData?.data) ? businessUnitsData.data : [];

  // Staff with exactly one (live) business unit always work in it and cannot change it.
  // Counted from the API list, not the profile, so a deleted unit on the profile is ignored.
  const lockedBusinessUnitId =
    isLimitedToBusinessUnits && businessUnits.length === 1 ? businessUnits[0].id ?? null : null;

  const businessUnitOptions = useMemo(() => {
    const options: { label: string; value: string }[] = [];
    if (!isLimitedToBusinessUnits) {
      options.push({ label: "No business unit", value: NO_BUSINESS_UNIT });
    }
    businessUnits.forEach((bu) => {
      options.push({ label: bu.name, value: String(bu.id) });
    });
    return options;
  }, [businessUnits, isLimitedToBusinessUnits]);

  const defaultValue: PriceMaster = {
    org_id: orgId,
    business_unit_id: lockedBusinessUnitId,
    rule_name: "",
    rule_code: "",
    item_type: ItemType.FacilitySlot,
    status: PriceMasterStatus.active,
    price: 0,
    reg_fee: 0,
    enable_plan_pricing: false,
    plan_pricing: [],
    enable_group_pricing: false,
    group_pricing: [],
    enable_special_pricing: false,
    special_pricing: [],
  };


  const form = useForm<PriceMaster>({
    mode: "all",
    defaultValues: defaultValue,
  });

  const {
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = form;

  const [createPriceMaster] = useCreatePriceMasterMutation();
  const [updatePriceMaster] = useUpdatePriceMasterMutation();

  const editId = action === "edit" && data?.id ? data.id : undefined;
  const { data: fetchedData, isFetching: isFetchingEdit } = useGetPriceMasterByIdQuery(
    { id: editId!, org_id: orgId },
    { skip: !editId || !isOpen }
  );

  useEffect(() => {
    if (!isOpen) return;
    if (action === "edit" && fetchedData) {
      reset({
        ...fetchedData,
        enable_plan_pricing: (fetchedData.plan_pricing?.length ?? 0) > 0,
        enable_group_pricing: (fetchedData.group_pricing?.length ?? 0) > 0,
        enable_special_pricing: (fetchedData.special_pricing?.length ?? 0) > 0,
      });
    } else if (action === "add") {
      reset(defaultValue);
    }
  }, [fetchedData, isOpen, action, orgId]);

  // The business units load after the drawer opens. Fill in the locked unit when it becomes
  // known without resetting the form, so anything already typed is kept.
  useEffect(() => {
    if (isOpen && action === "add" && lockedBusinessUnitId !== null) {
      form.setValue("business_unit_id", lockedBusinessUnitId);
    }
  }, [isOpen, action, lockedBusinessUnitId, form]);

  const onSubmit = async (rawData: PriceMaster) => {
    const formData = { ...rawData, rule_name: rawData.rule_name?.trim() };
    try {
      if (action === "add") {
        await createPriceMaster(formData).unwrap();
        toast({ variant: "success", title: "Price Master created successfully." });
      } else {
        await updatePriceMaster(formData).unwrap();
        toast({ variant: "success", title: "Price Master updated successfully." });
      }
      handleClose();
      refetch?.();
    } catch (error: unknown) {
      toast({
        variant: "destructive",
        title: "Error in form Submission",
        description: getApiErrorMessage(error),
      });
    }
  };

  const handleClose = () => {
    reset(defaultValue);
    setOpen(false);
    setData?.(undefined);
    setAction?.("add");
  };

  return (
    <Sheet open={isOpen} onOpenChange={handleClose}>
      <SheetContent
        className={cn("bg-formbg w-full p-0 custom-scrollbar h-full lg:!max-w-[1000px]")}
        hideCloseButton
      >
        <SheetHeader className="px-3 sm:px-5 sticky top-0 z-40 py-2 bg-white shadow-sm">
          <div className="flex justify-between items-center gap-2">
            <div className="flex items-center gap-3 min-w-0">
              <button type="button" onClick={handleClose}>
                <i className="fa fa-xmark text-xl"></i>
              </button>
              <h1 className="font-medium text-base sm:text-lg truncate">
                {readOnly ? "Preview Price Master Rule" : `${action === "add" ? "Create" : "Edit"} Price Master`}
                {isFetchingEdit && <span className="text-xs font-normal text-gray-500 ml-2 hidden sm:inline">(Loading...)</span>}
              </h1>
            </div>
            <div className="flex gap-2 shrink-0">
              <Button variant="outline" onClick={handleClose} className="h-8 px-2 sm:px-4">
                {readOnly ? "Close" : "Cancel"}
              </Button>
              {!readOnly && (
                <LoadingButton
                  onClick={handleSubmit(onSubmit)}
                  loading={isSubmitting}
                  className="bg-primary text-black h-8 px-3 sm:px-6"
                >
                  Save
                </LoadingButton>
              )}
            </div>
          </div>
        </SheetHeader>

        <FormProvider {...form}>
          <form className="p-3 sm:p-6 space-y-6">
            <Card className="p-3 sm:p-4 lg:p-6 shadow-sm border-l-4 border-l-primary bg-white">
              <div className="flex items-center gap-2 mb-3 sm:mb-4">
                <div className="p-2 bg-primary/10 rounded-lg text-primary">
                  <i className="fa-solid fa-circle-info text-sm"></i>
                </div>
                <div>
                  <h3 className="font-medium text-gray-900 text-sm italic">Basic Information</h3>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="rule_name"
                  rules={{
                    required: "Rule name is required",
                    validate: (value) =>
                      value?.trim() !== "" || "Rule name cannot be only spaces",
                  }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Rule Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter rule name" {...field} disabled={readOnly} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="item_type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Item Type</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value} disabled={readOnly}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select item type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {Object.values(ItemType).map((type) => (
                            <SelectItem key={type} value={type}>
                              <span className="capitalize">{type.replace("_", " ")}</span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="business_unit_id"
                  render={({ field }) => {
                    const selectedName = form.getValues("business_unit_name");
                    const currentValStr =
                      field.value != null ? String(field.value) : NO_BUSINESS_UNIT;
                    const matchedOption = businessUnitOptions.find(
                      (o) => o.value === currentValStr
                    );
                    const currentLabel =
                      matchedOption?.label ||
                      (field.value != null
                        ? selectedName || `Business unit #${field.value}`
                        : isLimitedToBusinessUnits
                        ? ""
                        : "No business unit");

                    return (
                      <FormItem>
                        <FormLabel>Business Unit</FormLabel>
                        <FormControl>
                          <Combobox
                            list={businessUnitOptions}
                            defaultValue={currentLabel}
                            setFilter={(selectedLabel: string) => {
                              const found = businessUnitOptions.find(
                                (o) => o.label === selectedLabel
                              );
                              if (found) {
                                field.onChange(
                                  found.value === NO_BUSINESS_UNIT
                                    ? null
                                    : Number(found.value)
                                );
                              }
                            }}
                            disabled={readOnly || lockedBusinessUnitId !== null}
                            showLabel={false}
                            className="w-full"
                          />
                        </FormControl>
                        {lockedBusinessUnitId !== null && !readOnly && (
                          <p className="text-xs text-muted-foreground">
                            You are assigned to one business unit, so it cannot be changed.
                          </p>
                        )}
                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />
              </div>
            </Card>

            <PricingConfig readOnly={readOnly} />
          </form>
        </FormProvider>
      </SheetContent>
    </Sheet>
  );
};

export default PriceMasterForm;
